import {
  Box,
  Typography,
  Stack,
  Paper,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TextField,
  InputAdornment,
  Tooltip,
  Select,
  FormControl,
  MenuItem,
} from '@mui/material';
import {
  SearchRounded,
  AccountBalanceRounded,
  CreditScoreRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useUiStore } from '../../../../stores/ui.store';
import { type FinancialRow, type ReportKpis } from '../../../../hooks/use-reports';

import { PlanDetailDialog } from './PlanDetailDialog';
import { KpiCard } from './KpiCard';
import { pct } from './shared';
import { fmt, PLAN_STATUS_COLOR, useTableWindowVirtualizer } from './shared';

/** Los estados de un plan, con su texto en `tuition.planStatusLabels.*`. */
const PLAN_STATUSES = ['active', 'completed', 'defaulted', 'withdrawn'] as const;

export function FinancialTab({
  rows,
  filtered,
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
  planFilter,
  setPlanFilter,
  programFilter,
  setProgramFilter,
  kpis,
  onOpenBreakdown,
}: {
  kpis: ReportKpis;
  /** Abre el desglose mensual, que vive en la página. */
  onOpenBreakdown: () => void;
  rows: FinancialRow[];
  filtered: FinancialRow[];
  search: string;
  setSearch: (s: string) => void;
  statusFilter: string;
  setStatusFilter: (s: string) => void;
  planFilter: string;
  setPlanFilter: (s: string) => void;
  programFilter: string;
  setProgramFilter: (s: string) => void;
}) {
  const { t } = useTranslation('sis');
  const { colorMode } = useUiStore();
  const isDark = colorMode === 'dark';
  const [selectedPlan, setSelectedPlan] = useState<FinancialRow | null>(null);
  const { containerRef, virtualizer, items, paddingTop, paddingBottom } = useTableWindowVirtualizer(
    filtered.length,
  );

  // Collect unique plan types and programs for filter dropdowns
  const planTypeOptions = useMemo(() => {
    const types = new Set(rows.map((r) => r.planType).filter((t) => t && t !== '—'));
    return Array.from(types).sort();
  }, [rows]);
  const programOptions = useMemo(() => {
    const progs = new Set(rows.map((r) => r.programName).filter((p) => p && p !== '—'));
    return Array.from(progs).sort();
  }, [rows]);

  const fmtRow = (n: number, currency: string) => fmt(n, currency);

  return (
    <>
      {/* Los indicadores viven DENTRO de su pestaña. En la cabecera afirmaban
          ser relevantes para las seis: quien miraba Cobertura veía "Monto
          vencido", que no tiene nada que ver con lo que estaba haciendo. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <KpiCard
          isDark={isDark}
          icon={<CreditScoreRounded fontSize="inherit" />}
          label={t('reports.financial.kpis.withPlan')}
          value={kpis.withActivePlan.toString()}
          sub={t('reports.financial.kpis.ofTotal', { pct: pct(kpis.planRate) })}
        />
        <KpiCard
          isDark={isDark}
          icon={<AccountBalanceRounded fontSize="inherit" />}
          label={t('reports.financial.kpis.collected')}
          value={fmt(kpis.totalCollected, kpis.currency)}
          variant="success"
        />
        <KpiCard
          isDark={isDark}
          icon={<AccountBalanceRounded fontSize="inherit" />}
          label={t('reports.financial.kpis.toCollect')}
          value={fmt(kpis.totalCommitted, kpis.currency)}
          onClick={onOpenBreakdown}
        />
        <KpiCard
          isDark={isDark}
          icon={<WarningAmberRounded fontSize="inherit" />}
          label={t('reports.financial.kpis.overdue')}
          value={fmt(kpis.overdueAmount, kpis.currency)}
          variant={kpis.overdueAmount > 0 ? 'warning' : 'default'}
          sub={t(
            kpis.overdueAmount > 0
              ? 'reports.financial.kpis.overdueSub'
              : 'reports.financial.kpis.noOverdue',
          )}
        />
      </Box>

      {/* Filters */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        mb={2}
        alignItems={{ sm: 'center' }}
      >
        <TextField
          size="small"
          placeholder={t('reports.financial.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select value={programFilter} onChange={(e) => setProgramFilter(e.target.value)}>
            <MenuItem value="all">{t('reports.financial.allPrograms')}</MenuItem>
            {programOptions.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="all">{t('reports.financial.allStatuses')}</MenuItem>
            {PLAN_STATUSES.map((st) => (
              <MenuItem key={st} value={st}>
                {t(`tuition.planStatusLabels.${st}`)}
              </MenuItem>
            ))}
            <MenuItem value="__exempt__">{t('reports.financial.exempt')}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
            <MenuItem value="all">{t('reports.financial.allPlans')}</MenuItem>
            {planTypeOptions.map((pt) => (
              <MenuItem key={pt} value={pt}>
                {pt}
              </MenuItem>
            ))}
            <MenuItem value="__no_plan__">{t('reports.financial.noPlan')}</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          {t('reports.financial.planCount', { count: filtered.length })}
        </Typography>
      </Stack>

      <TableContainer
        ref={containerRef}
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 2, overflowX: 'auto' }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { color: 'text.secondary', fontWeight: 600, fontSize: 12 } }}>
              <TableCell>{t('reports.financial.columns.student')}</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                {t('reports.financial.columns.program')}
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                {t('reports.financial.columns.plan')}
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                {t('reports.financial.columns.paid')}
              </TableCell>
              <TableCell>{t('reports.financial.columns.pending')}</TableCell>
              <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                {t('reports.financial.columns.nextDue')}
              </TableCell>
              <TableCell>{t('reports.financial.columns.status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No hay planes de pago con los filtros actuales
                </TableCell>
              </TableRow>
            ) : (
              <>
                {paddingTop > 0 && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0, border: 0, height: paddingTop }} />
                  </TableRow>
                )}
                {items.map((vItem) => {
                  const row = filtered[vItem.index];
                  if (!row) return null;
                  return (
                    <TableRow
                      key={row.planId}
                      data-index={vItem.index}
                      ref={virtualizer.measureElement}
                      hover
                      onClick={() => setSelectedPlan(row)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography fontWeight={500} fontSize={13}>
                          {row.studentName || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography
                          variant="body2"
                          color={row.programName !== '—' ? 'text.primary' : 'text.disabled'}
                          fontWeight={row.programName !== '—' ? 500 : 400}
                        >
                          {row.programName}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2">{row.planType}</Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" color="success.main">
                          {fmtRow(row.paidAmount, row.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack>
                          <Typography
                            variant="body2"
                            color={row.overdueAmount > 0 ? 'warning.main' : 'text.primary'}
                            fontWeight={row.overdueAmount > 0 ? 700 : 400}
                          >
                            {fmtRow(row.pendingAmount, row.currency)}
                          </Typography>
                          {row.overdueAmount > 0 && (
                            <Typography variant="caption" color="warning.main">
                              ⚠{' '}
                              {t('reports.financial.overdueRow', {
                                amount: fmtRow(row.overdueAmount, row.currency),
                              })}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary">
                          {row.nextDueDate ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.isExempt ? (
                          <Tooltip
                            title={row.exemptionReason ?? t('reports.financial.exemptDefault')}
                          >
                            <Chip
                              size="small"
                              label={t('reports.financial.exempt')}
                              color="warning"
                              variant="filled"
                              sx={{ fontSize: 11 }}
                            />
                          </Tooltip>
                        ) : (
                          <Chip
                            size="small"
                            label={
                              (PLAN_STATUSES as readonly string[]).includes(row.status)
                                ? t(`tuition.planStatusLabels.${row.status}`)
                                : row.status
                            }
                            color={PLAN_STATUS_COLOR[row.status] ?? 'default'}
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {paddingBottom > 0 && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0, border: 0, height: paddingBottom }} />
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <PlanDetailDialog plan={selectedPlan} onClose={() => setSelectedPlan(null)} isDark={isDark} />
    </>
  );
}
